import React, { useEffect, useMemo, useRef } from 'react';
import { styled } from '@withjoy/joykit';
import { MenuItem } from './MenuItem';
import { Search } from './Search';
import type { INavTheme } from './NavThemes';
import { useFeatureValue } from '@shared/core/featureFlags';
import { EventFeature, useProFeature } from '@shared/hooks/useProFeature';
import { getConfig } from '@config';

const MenuContainer = styled.div<{}>`
  display: grid;
  grid-template-columns: 1fr 1fr 1fr;
  grid-gap: space-evenly;
  justify-items: center; /* Centers content horizontally within each cell */
  align-items: start; /* Aligns content vertically at the start of each cell */
`;

const Wrapper = styled.div``;

export interface IMenuItem {
  // Used for UX display
  label: string;
  // URL based navigation for items not in joy-web SPA
  link?: string;
  // Joy-web SPA navigation, prefer over link
  route?: string;
  appId?: string;
  color: {
    start: string;
    end: string;
  };
  background?: string;
  description?: string;
  keywords: string[];
  subNavigation?: IMenuItem[];
  iconUrl?: string;
}

interface WorkspaceMenuProps {
  menuItems: IMenuItem[];
  onMenuItemClick?: (item: IMenuItem) => void;
  eventId: string;
  parentSearchResults?: IMenuItem[];
  theme: INavTheme;
  showSearch?: boolean;
}

const DEFAULT_TERMS: string[] = [];
const DEFAULT_RESULTS: IMenuItem[] = [];

const searchRoutes = async (searchTerms: string[], menuItems: IMenuItem[]): Promise<IMenuItem[]> => {
  if (!searchTerms || searchTerms.length === 0) {
    return DEFAULT_RESULTS; // Return all items if no search terms are provided
  }

  // Convert search terms to lowercase for case-insensitive matching
  const normalizedTerms = searchTerms?.map(term => term?.toLowerCase()) ?? [];

  // Filter routes that match any of the search terms
  return menuItems.filter(route => {
    // Check if any keyword matches
    const keywordMatch = route.keywords.some(keyword => normalizedTerms.some(term => keyword.toLowerCase().includes(term)));

    // Check if title matches
    const titleMatch = normalizedTerms.some(term => route.label?.toLowerCase().includes(term));

    // Check if description matches
    const descriptionMatch = normalizedTerms.some(term => route.description?.toLowerCase().includes(term));

    return keywordMatch || titleMatch || descriptionMatch;
  });
};

const carbonNavItem: IMenuItem = {
  appId: 'planner',
  label: 'AI Planner',
  link: `/${getConfig().carbonBasePath}/create`,
  color: {
    start: 'rgba(82, 146, 241, 1)',
    end: 'rgba(38, 100, 194, 1)',
  },
  background: 'linear-gradient(155.59deg, #5292F1 -6.99%, #306DCA 84.39%)',
  description: 'Your AI planning assistant',
  keywords: ['planning', 'assistant', 'ai', 'event'],
  iconUrl: 'https://withjoy.com/assets/public/pro/icons/event_site_v1.svg',
};

export const WorkspaceMenu: React.FC<WorkspaceMenuProps> = ({ menuItems, onMenuItemClick, theme, showSearch = false, eventId }) => {
  const [searchTerms, setSearchTerms] = React.useState<string[]>(DEFAULT_TERMS);
  const [searchProcessing, setSearchProcessing] = React.useState(false);
  const [searchResults, setSearchResults] = React.useState<IMenuItem[]>(DEFAULT_RESULTS);
  const exposeCarbonNavigation = useFeatureValue('joyProExposeCarbon');
  const isCarbonEnabled = useProFeature(EventFeature.carbonEnabled);

  const adjustedMenuItems = useMemo(() => {
    if (!isCarbonEnabled || exposeCarbonNavigation?.value !== 'on') {
      return menuItems;
    }

    const carbonLink = `/${getConfig().carbonBasePath}/edit/event/${eventId}`;
    const enrichedItem = { ...carbonNavItem, link: carbonLink };

    return [...menuItems, enrichedItem];
  }, [eventId, exposeCarbonNavigation?.value, menuItems, isCarbonEnabled]);

  const searchActive = useRef(false);
  const handleSearchInputChange = (value: string | null) => {
    setSearchTerms(value ? value.split(' ').filter(term => term.trim() !== '') : []);
  };

  const handleSearchSubmit = (value: string | null) => {
    setSearchTerms(value ? value.split(' ').filter(term => term.trim() !== '') : DEFAULT_TERMS);
    setSearchProcessing(true);
  };

  const handleSearchCancel = () => {
    setSearchTerms([]);
    searchActive.current = false;
    setSearchResults([]);
    setSearchProcessing(false);
  };

  useEffect(() => {
    if (!searchActive.current && searchProcessing) {
      searchActive.current = true;
      const searchRoutesWrapper = async () => {
        const searchResults = await searchRoutes(searchTerms, adjustedMenuItems);
        searchActive.current = false;
        setSearchProcessing(false);
        setSearchResults(searchResults);
      };
      searchRoutesWrapper();
    }
  }, [searchProcessing, searchTerms, adjustedMenuItems]);

  return (
    <Wrapper>
      {showSearch && (
        <Search
          style={{ margin: '16px auto', marginBottom: '40px' }}
          onInput={handleSearchInputChange}
          onSubmit={handleSearchSubmit}
          onCancel={handleSearchCancel}
          processing={searchProcessing}
        />
      )}
      <MenuContainer>
        {searchTerms.length > 0 && searchResults.length > 0 && (
          <>
            {searchResults.map((item, index) => (
              <MenuItem
                key={index}
                label={item.label}
                theme={theme}
                color={item.color}
                background={item.background}
                link={item.link}
                route={item.route}
                description={item.description}
                keywords={item.keywords}
                iconUrl={item.iconUrl}
                onMenuItemClick={onMenuItemClick}
              />
            ))}
          </>
        )}
        {adjustedMenuItems.map((item, index) => (
          <MenuItem
            key={index}
            label={item.label}
            color={item.color}
            background={item.background}
            theme={theme}
            link={item.link}
            route={item.route}
            description={item.description}
            keywords={item.keywords}
            iconUrl={item.iconUrl}
            onMenuItemClick={onMenuItemClick}
          />
        ))}
      </MenuContainer>
    </Wrapper>
  );
};
